<template>
  <div class="teacher-management">
    <el-row :gutter="20">
      <!-- Subject Management -->
      <el-col :span="8">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>科目管理</span>
              <el-button type="primary" size="small" @click="showSubjectDialog">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-button>
            </div>
          </template>
          <el-table :data="subjects" v-loading="loadingSubjects" size="small">
            <el-table-column prop="name" label="科目名称" />
            <el-table-column label="操作" width="80">
              <template #default="{ row }">
                <el-popconfirm title="确定删除？" @confirm="deleteSubject(row.id)">
                  <template #reference>
                    <el-button text type="danger" size="small">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- Teacher List -->
      <el-col :span="16">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>教师列表</span>
              <div class="header-actions">
                <el-button @click="showBatchImportDialog">
                  <el-icon>
                    <MagicStick />
                  </el-icon>
                  批量导入
                </el-button>
                <el-button type="primary" @click="showTeacherDialog">
                  <el-icon>
                    <Plus />
                  </el-icon>
                  新建教师
                </el-button>
              </div>
            </div>
          </template>

          <el-table :data="teachers" v-loading="loadingTeachers" stripe>
            <el-table-column prop="name" label="姓名" width="120" />
            <el-table-column prop="department" label="部门" width="120" />
            <el-table-column label="任教科目" min-width="150">
              <template #default="{ row }">
                <el-tag v-for="subject in row.subjects" :key="subject.id" size="small" style="margin-right: 4px">
                  {{ subject.name }}
                </el-tag>
                <span v-if="!row.subjects?.length" style="color: #6b7280">-</span>
              </template>
            </el-table-column>
            <el-table-column label="任教年级" width="150">
              <template #default="{ row }">
                <el-tag v-for="(grade, idx) in row.grades" :key="idx" type="info" size="small"
                  style="margin-right: 4px">
                  {{ grade }}
                </el-tag>
                <span v-if="!row.grades?.length" style="color: #6b7280">-</span>
              </template>
            </el-table-column>
            <el-table-column label="最大监考" width="100">
              <template #default="{ row }">
                <el-tag v-if="row.max_hours" type="warning" size="small">{{ row.max_hours }}h</el-tag>
                <span v-else style="color: #9ca3af">无限制</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template #default="{ row }">
                <el-button text type="primary" @click="editTeacher(row)">编辑</el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteTeacher(row.id)">
                  <template #reference>
                    <el-button text type="danger">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- Subject Dialog -->
    <el-dialog v-model="subjectDialogVisible" title="新建科目" width="400px">
      <el-form :model="subjectForm" label-width="80px">
        <el-form-item label="科目名称">
          <el-input v-model="subjectForm.name" placeholder="如：语文、数学" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="subjectDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitSubject" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Teacher Dialog -->
    <el-dialog v-model="teacherDialogVisible" :title="isEdit ? '编辑教师' : '新建教师'" width="550px">
      <el-form :model="teacherForm" label-width="100px" :rules="teacherRules" ref="teacherFormRef">
        <el-form-item label="工号" prop="employee_id">
          <el-input v-model="teacherForm.employee_id" placeholder="教师工号" />
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="teacherForm.name" placeholder="教师姓名" />
        </el-form-item>
        <el-form-item label="部门">
          <el-input v-model="teacherForm.department" placeholder="所属部门/教研组" />
        </el-form-item>
        <el-form-item label="任教科目">
          <el-select v-model="teacherForm.subject_ids" multiple placeholder="选择任教科目" style="width: 100%">
            <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="任教年级">
          <el-select v-model="teacherForm.grades" multiple allow-create filterable placeholder="选择或输入年级"
            style="width: 100%">
            <el-option label="高一" value="高一" />
            <el-option label="高二" value="高二" />
            <el-option label="高三" value="高三" />
            <el-option label="初一" value="初一" />
            <el-option label="初二" value="初二" />
            <el-option label="初三" value="初三" />
          </el-select>
        </el-form-item>
        <el-form-item label="最大监考时长">
          <el-input-number v-model="teacherForm.max_hours" :min="0" :step="0.5" :precision="1" placeholder="留空表示不限制" />
          <span style="margin-left: 8px; color: #6b7280">小时/学期，留空表示不限制</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="teacherDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTeacher" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Dialog -->
    <el-dialog v-model="batchImportDialogVisible" title="批量导入教师" width="900px">
      <div class="batch-import-container">
        <div class="import-instructions">
          <p>请粘贴教师信息，每行一个，格式：<strong>姓名 部门 任教科目 任教年级</strong>（空格或制表符分隔）</p>
          <p>年级支持多个，用逗号或空格分隔。例如：<strong>高一,高二</strong> 或 <strong>高一 高二</strong></p>
          <p class="example">示例：<br />张三 语文组 语文 高一,高二<br />李四 数学组 数学 高三</p>
        </div>

        <el-input v-model="importText" type="textarea" :rows="8" placeholder="在此粘贴教师信息..." @input="parseImportText" />

        <div v-if="parsedTeachers.length > 0" class="preview-section">
          <h4>解析预览 ({{ parsedTeachers.length }} 位教师)</h4>
          <el-table :data="parsedTeachers" size="small" border height="300">
            <el-table-column prop="name" label="姓名" width="100" />
            <el-table-column prop="department" label="部门" width="120" />
            <el-table-column prop="subject_name" label="科目" width="100" />
            <el-table-column label="年级">
              <template #default="{ row }">
                <el-tag v-for="g in row.grades" :key="g" size="small" style="margin-right: 4px">{{ g }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <template #footer>
        <el-button @click="batchImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchImport" :loading="submitting"
          :disabled="parsedTeachers.length === 0">
          确定导入
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Plus, MagicStick } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { teacherApi } from '@/api'

const subjects = ref([])
const teachers = ref([])
const loadingSubjects = ref(false)
const loadingTeachers = ref(false)
const submitting = ref(false)

const subjectDialogVisible = ref(false)
const teacherDialogVisible = ref(false)
const batchImportDialogVisible = ref(false)
const isEdit = ref(false)
const editingId = ref(null)
const teacherFormRef = ref(null)

const importText = ref('')
const parsedTeachers = ref([])

const subjectForm = ref({ name: '' })
const teacherForm = ref({
  employee_id: '',
  name: '',
  department: '',
  subject_ids: [],
  grades: [],
  max_hours: null
})

const teacherRules = {
  name: [{ required: true, message: '请输入姓名', trigger: 'blur' }]
}

const fetchSubjects = async () => {
  loadingSubjects.value = true
  try {
    subjects.value = await teacherApi.getSubjects()
  } catch (e) {
    console.error(e)
  } finally {
    loadingSubjects.value = false
  }
}

const fetchTeachers = async () => {
  loadingTeachers.value = true
  try {
    teachers.value = await teacherApi.getAll()
  } catch (e) {
    console.error(e)
  } finally {
    loadingTeachers.value = false
  }
}

const showSubjectDialog = () => {
  subjectForm.value = { name: '' }
  subjectDialogVisible.value = true
}

const submitSubject = async () => {
  if (!subjectForm.value.name) {
    ElMessage.warning('请输入科目名称')
    return
  }
  submitting.value = true
  try {
    await teacherApi.createSubject(subjectForm.value)
    ElMessage.success('科目创建成功')
    subjectDialogVisible.value = false
    fetchSubjects()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const deleteSubject = async (id) => {
  try {
    await teacherApi.deleteSubject(id)
    ElMessage.success('科目已删除')
    fetchSubjects()
  } catch (e) {
    console.error(e)
  }
}

const showTeacherDialog = () => {
  isEdit.value = false
  editingId.value = null
  teacherForm.value = {
    employee_id: '',
    name: '',
    department: '',
    subject_ids: [],
    grades: [],
    max_hours: null
  }
  teacherDialogVisible.value = true
}

const editTeacher = (row) => {
  isEdit.value = true
  editingId.value = row.id
  teacherForm.value = {
    employee_id: row.employee_id,
    name: row.name,
    department: row.department || '',
    subject_ids: row.subjects?.map(s => s.id) || [],
    grades: row.grades || [],
    max_hours: row.max_hours || null
  }
  teacherDialogVisible.value = true
}

const submitTeacher = async () => {
  if (!teacherFormRef.value) return
  await teacherFormRef.value.validate(async (valid) => {
    if (!valid) return

    submitting.value = true
    try {
      if (isEdit.value) {
        await teacherApi.update(editingId.value, teacherForm.value)
        ElMessage.success('教师信息已更新')
      } else {
        await teacherApi.create(teacherForm.value)
        ElMessage.success('教师创建成功')
      }
      teacherDialogVisible.value = false
      fetchTeachers()
    } catch (e) {
      console.error(e)
    } finally {
      submitting.value = false
    }
  })
}

const deleteTeacher = async (id) => {
  try {
    await teacherApi.delete(id)
    ElMessage.success('教师已删除')
    fetchTeachers()
  } catch (e) {
    console.error(e)
  }
}

const showBatchImportDialog = () => {
  importText.value = ''
  parsedTeachers.value = []
  batchImportDialogVisible.value = true
}

const parseImportText = () => {
  if (!importText.value.trim()) {
    parsedTeachers.value = []
    return
  }

  const lines = importText.value.split('\n')
  const result = []

  lines.forEach(line => {
    if (!line.trim()) return

    // Split by whitespace
    const parts = line.trim().split(/\s+/)

    if (parts.length >= 4) {
      const name = parts[0]
      const department = parts[1]
      const subject_name = parts[2]

      // The rest are grades. They might be separated by commas or spaces.
      // If the user used commas like "高一,高二", parts[3] will be "高一,高二".
      // If the user used spaces like "高一 高二", parts[3] is "高一", parts[4] is "高二".
      const remaining = parts.slice(3).join(' ')
      const grades = remaining.split(/[\s,，]+/).filter(g => g.length > 0)

      result.push({
        name,
        department,
        subject_name,
        grades
      })
    }
  })

  parsedTeachers.value = result
}

const submitBatchImport = async () => {
  if (parsedTeachers.value.length === 0) return

  submitting.value = true
  try {
    const res = await teacherApi.batchImport({ teachers: parsedTeachers.value })
    ElMessage.success(res.message || '导入成功')
    batchImportDialogVisible.value = false
    fetchTeachers()
    fetchSubjects() // In case new subjects were created
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchSubjects()
  fetchTeachers()
})
</script>

<style scoped>
.teacher-management {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.batch-import-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.import-instructions {
  background-color: #f8fafc;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #475569;
}

.import-instructions p {
  margin: 4px 0;
}

.import-instructions .example {
  color: #94a3b8;
  font-family: monospace;
  margin-top: 8px;
}

.preview-section h4 {
  margin: 0 0 12px 0;
  color: #1e293b;
}
</style>
